/**
 * Executes a custom script string provided in a tracker event configuration.
 * Errors are swallowed so a bad script never breaks the booking flow.
 *
 * @param {string|null|undefined} script
 */
export const executeEventScript = (script) => {
    if (!script || typeof script !== 'string' || script.trim() === '') return;
    try {
        // eslint-disable-next-line no-new-func
        new Function(script)();
    } catch (e) {
        console.error('[Tracker] Error executing event script:', e);
    }
};

/**
 * Sends tracker events for a given trigger name across all configured providers.
 * Executes the optional custom script attached to each matching event.
 *
 * @param {string} triggerName   – one of the WEBSITE_TRACKER_EVENT_TRIGGEERS keys
 * @param {{ trackers: Array, data?: { meta?: object } }} params
 */
export const sendTrackerEvents = (triggerName, { trackers, data } = {}) => {
    if (!Array.isArray(trackers) || trackers.length === 0) return;

    const snapTracker = trackers.find((t) => t?.provider === 'snapchat');
    const fcbkTracker = trackers.find((t) => t?.provider === 'facebook');
    const gtagTracker = trackers.find((t) => t?.provider === 'googletagmanager');

    snapTracker?.events
        ?.filter((evt) => evt?.action_trigger === triggerName)
        ?.forEach((event) => {
            if (typeof snaptr !== 'undefined') snaptr('track', event.event_name); // eslint-disable-line no-undef
            executeEventScript(event.script);
        });

    fcbkTracker?.events
        ?.filter((evt) => evt?.action_trigger === triggerName)
        ?.forEach((event) => {
            if (typeof fbq !== 'undefined') { // eslint-disable-line no-undef
                if (data?.meta) {
                    fbq('track', event.event_name, data.meta); // eslint-disable-line no-undef
                } else {
                    fbq('track', event.event_name); // eslint-disable-line no-undef
                }
            }
            executeEventScript(event.script);
        });

    gtagTracker?.events
        ?.filter((evt) => evt?.action_trigger === triggerName)
        ?.forEach((event) => {
            if (Array.isArray(window.dataLayer)) {
                window.dataLayer.push({ event: event.event_name });
            }
            executeEventScript(event.script);
        });
};

/**
 * Formats ecommerce data from a booking object for GTM/GA4 purchase events.
 *
 * @param {object} booking
 * @returns {{ coupon: string, tax: number, items: Array }|null}
 */
export const formatPurchaseEcommerceData = (booking) => {
    if (!booking?.invoicing_data) { return null; }

    const ecommerceData = {
        coupon: '',
        tax: 0,
        items: []
    };

    if (booking?.coupons) {
        ecommerceData.coupon = booking?.coupons?.[0]?.code ?? '';
    }

    ecommerceData.tax = booking?.invoicing_data?.taxes?.reduce(
        (acc, tax) => acc + tax.tax_amount, 0
    ) ?? 0;

    booking?.invoicing_data?.products?.forEach((product) => {
        const item = {
            price: Number(Number(product?.unit_amount_ht)?.toFixed(2)),
            quantity: product?.quantity,
            item_id: `${product?.related_object_id ?? product?.external_booking_product_id}`,
            item_name: product?.name
        };
        ecommerceData.items.push(item);
    });

    return ecommerceData;
};

/**
 * Builds the dataLayer payload for a GTM purchase event.
 *
 * @param {{ eventName: string, booking: object, currency: string }} params
 * @returns {object}
 */
export const buildPurchaseDataLayerPayload = ({ eventName, booking, currency = 'EUR' }) => {
    const payload = {
        event: eventName,
        transaction_id: booking?.reference,
        value: booking?.booking_incl_amount,
        currency,
    };

    const ecommerceData = formatPurchaseEcommerceData(booking);
    if (ecommerceData) {
        payload.ecommerce = ecommerceData;
    }

    return payload;
};

/**
 * Builds the fbq payload for a Meta (Facebook) Purchase event.
 *
 * @param {{ booking: object, currency: string }} params
 * @returns {object}
 */
export const buildFacebookPurchasePayload = ({ booking, currency = 'EUR' }) => {
    const payload = {
        value: booking?.booking_incl_amount ?? 0,
        currency,
        content_type: 'product',
    };

    const products = booking?.invoicing_data?.products;
    if (Array.isArray(products) && products.length > 0) {
        payload.content_ids = products.map(
            (product) => `${product?.related_object_id ?? product?.external_booking_product_id}`
        );
        payload.num_items = products.reduce((acc, product) => acc + (product?.quantity ?? 0), 0);
    }

    return payload;
};

/**
 * Pushes purchase events to window.dataLayer for GTM trackers.
 * Also fires snaptr / fbq for Snapchat and Facebook trackers.
 * Executes the optional custom script attached to each matching event.
 *
 * @param {{ trackers: Array, booking: object, currency: string }} params
 */
export const triggerPurchaseDataLayerEvents = ({ trackers, booking, currency = 'EUR' }) => {
    if (!Array.isArray(trackers) || trackers.length === 0) return;

    const snapTracker = trackers.find((t) => t?.provider === 'snapchat');
    const fcbkTracker = trackers.find((t) => t?.provider === 'facebook');
    const gtagTracker = trackers.find((t) => t?.provider === 'googletagmanager');

    snapTracker?.events
        ?.filter((evt) => evt?.action_trigger === 'confirm_payment')
        ?.forEach((event) => {
            if (typeof snaptr !== 'undefined') snaptr('track', event.event_name); // eslint-disable-line no-undef
            executeEventScript(event.script);
        });

    fcbkTracker?.events
        ?.filter((evt) => evt?.action_trigger === 'confirm_payment')
        ?.forEach((event) => {
            if (typeof fbq !== 'undefined') { // eslint-disable-line no-undef
                fbq('track', event.event_name, buildFacebookPurchasePayload({ booking, currency })); // eslint-disable-line no-undef
            }
            executeEventScript(event.script);
        });

    gtagTracker?.events
        ?.filter((evt) => evt?.action_trigger === 'confirm_payment')
        ?.forEach((event) => {
            if (Array.isArray(window.dataLayer)) {
                const payload = buildPurchaseDataLayerPayload({ eventName: event.event_name, booking, currency });
                window.dataLayer.push(payload);
            }
            executeEventScript(event.script);
        });
};
